/*
 * See the file LICENSE for the full license governing this code.
 */

import * as React from 'react';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import MuiTextField from '@mui/material/TextField';
import isAlphanumeric from 'validator/es/lib/isAlphanumeric';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import MuiSelect from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import FormHelperText from '@mui/material/FormHelperText';
import _ from 'lodash';
import Loader from '../Loader';
import { networkWithErrorConvert, useQuery } from '../../api';

const OptionsContext = React.createContext();

const useOptionsContext = () => React.useContext(OptionsContext);

const TextFieldPrebind = (name) =>
  function TextField() {
    const {
      [name]: { value, set, invalidMessage },
    } = useOptionsContext();
    return (
      <MuiTextField
        data-cy={name}
        error={Boolean(invalidMessage)}
        value={value}
        onChange={set}
        label={name}
        helperText={invalidMessage}
      />
    );
  };

const SelectPrebind = (name, options) =>
  function Select() {
    const {
      [name]: { value, set, invalidMessage },
    } = useOptionsContext();
    return (
      <FormControl
        data-cy={`form-control-${name}`}
        fullWidth
        error={Boolean(invalidMessage)}
        disabled={options.length === 1}
      >
        <InputLabel>{name}</InputLabel>
        <MuiSelect value={value} label={name} onChange={set}>
          {options.map(({ value, title }) => (
            <MenuItem key={value} value={value}>
              {title}
            </MenuItem>
          ))}
        </MuiSelect>
        <FormHelperText>
          {options.length === 1 ? 'Other options coming soon!' : invalidMessage}
        </FormHelperText>
      </FormControl>
    );
  };

const clusterDialogOptions = [
  {
    name: 'Name',
    keyInBody: 'display-name',
    initValue: () => `agraph-${_.random(1000)}`,
    invalidMessageFn: (title) => {
      const locale = 'en-US';
      if (!isAlphanumeric(title, locale, { ignore: '-_' })) {
        return 'Expected latin letters, numbers, hyphen, underscore';
      }
      if (title.length > 50) {
        return 'Expected title length to be less than 50 characters';
      }
      return '';
    },
    Component: TextFieldPrebind('Name'),
  },
];

const emptyStringFn = () => '';

function OptionsContextProvider({ children }) {
  const [inputs, setInputs] = React.useState({});
  const addInputs = (newInputs) => {
    setInputs((oldInputsObj) => {
      const newInputsObj = {};
      for (const {
        name,
        initValue: initValueOrFn,
        invalidMessageFn = emptyStringFn,
        ...rest
      } of newInputs) {
        if (!oldInputsObj[name]) {
          const set = (e) => {
            const newValue = e.target.value;
            setInputs((oldInputs) => ({
              ...oldInputs,
              [name]: {
                ...oldInputs[name],
                value: newValue,
                invalidMessage: invalidMessageFn(newValue),
              },
            }));
          };
          const initValue = _.isFunction(initValueOrFn)
            ? initValueOrFn()
            : initValueOrFn;
          newInputsObj[name] = {
            ...rest,
            name,
            value: initValue,
            set,
            reset: () => set({ target: { value: initValue } }),
            invalidMessage: invalidMessageFn(initValue),
          };
        }
      }
      return {
        ...oldInputsObj,
        ...newInputsObj,
      };
    });
  };
  const value = {
    ...inputs,
    isInvalid: Object.values(inputs)
      .map(({ invalidMessage }) => invalidMessage)
      .some(Boolean),
    addInputs,
    reset: () => Object.values(inputs).forEach(({ reset }) => reset()),
  };
  return (
    <OptionsContext.Provider value={value}>{children}</OptionsContext.Provider>
  );
}

function usePrevious(value) {
  const ref = React.useRef();
  React.useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

function NewClusterDialog({ isOpen, close: closeDialog, createCluster }) {
  const { isInvalid, reset, addInputs, ...inputs } = useOptionsContext();
  const queryOptions = useQuery(['cluster-options'], async () => {
    const res = await networkWithErrorConvert({ url: '/v1/cluster/options' });
    const selectClusterOptions = res.map(
      ({ name, label, options: inputOptions }) => {
        const options = inputOptions.map(({ value, label }) => ({
          value,
          title: label,
        }));
        return {
          name: label,
          keyInBody: name,
          initValue: options[0].value,
          Component: SelectPrebind(label, options),
        };
      }
    );
    addInputs(clusterDialogOptions.concat(selectClusterOptions));
    return null;
  });
  const prevOpenState = usePrevious(isOpen);

  React.useEffect(() => {
    if (prevOpenState === false && isOpen === true) {
      reset();
    }
  }, [isOpen, reset, prevOpenState]);
  const handleEntering = () => {};

  return (
    <Dialog TransitionProps={{ onEntering: handleEntering }} open={isOpen}>
      <DialogTitle sx={{ pr: 1.5 }}>Server options</DialogTitle>
      <DialogContent dividers>
        {queryOptions.isSuccess ? (
          <Stack spacing={2}>
            {Object.values(inputs).map(({ Component, name }) => (
              <Component key={name} />
            ))}
          </Stack>
        ) : (
          <Loader />
        )}
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={closeDialog}>
          Cancel
        </Button>
        <Button
          disabled={isInvalid}
          data-cy="create-cluster-button"
          onClick={() => {
            createCluster(
              Object.assign(
                {},
                ...Object.values(inputs).map(({ keyInBody, name }) => ({
                  [keyInBody]: inputs[name].value,
                }))
              )
            );
            closeDialog();
          }}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function NewClusterDialogWithContext(props) {
  return (
    <OptionsContextProvider>
      <NewClusterDialog {...props} />
    </OptionsContextProvider>
  );
}
